// Angular
import {
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

// Thirt party
import { EMPTY, catchError, take, throwError } from 'rxjs';

// Local library
import {
  GUEST,
  PAGES_WITH_401_STATUS_CODE_BUT_SKIP_WAGER_REDIRECT,
  SERVER_ERROR_MESSAGE,
  apiWithNoFloatingMessageList,
  apiWithNoUserTokenNeededList
} from '@amwager/util-ts';

// Local file
import { SessionService } from './session.service';
import { BASE_URL } from '../tokens';
import { CommonFacade } from '../+state';
import { ToastController } from '@ionic/angular/standalone';
import { CancelApiService } from './cancel-api.service';



/**
 * @description
 *
 * For testing purposes.
 */
declare global {
  interface Window {
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    Cypress: any
  }
}

// temp for user auth only
const urlChecker = (url: string) => {
  let version = '2.0';
  const urlToCheck: string[] = [
    // 'Login',
    // 'Signup',
    // 'VerifiyIdentity',
    // 'UploadDocuments',
    // 'RenewPassword',
    // 'UpdatePassword',
    // 'Logout'
    // 'StackSingleTicket',
    'GetRCNReplayVideosByFilters',
    // 'GetRCNReplayVideoLink'
    'LossLimit'
  ];
  for (const path of urlToCheck) {
      // TODO: PMM-202 temporary use the api version 1 for StackSingleTicket
      // version 2: issue with the payload
      // after the issue with the payload is fixed: use version 2
      if (url.indexOf(path) !== -1) version = '1.0';
  }
  return version;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const headers = (service: any, request: any, isGuest = false, removeToken = false)
: {setHeaders: any} => {

  // temp for user auth only
  const apiVersion = urlChecker(request.url);
  // const apiVersion = '2.0';

  if (isGuest) {
    return {
      setHeaders:  {
        'accept': 'application/json',
        'content-type': 'application/json',
        'my-custom': 'httpInterceptor',
        'token': removeToken ? '' : service.sessionService.getUserToken(),
        'api-version': apiVersion,
        'isGuest': String(isGuest)
      }
    }
  }
  else {
    return {
      setHeaders:  {
        'accept': 'application/json',
        'content-type': 'application/json',
        'my-custom': 'httpInterceptor',
        'token': removeToken ? '' : service.sessionService.getUserToken(),
        'api-version': apiVersion,
      }
    }
  }

  // return {
  //   setHeaders:  {
  //     'accept': 'application/json',
  //     'content-type': 'application/json',
  //     'my-custom': 'httpInterceptor',
  //     'token': removeToken ? '' : service.sessionService.getUserToken(),
  //     'api-version': apiVersion,
  //     'isGuest': String(isGuest)
  //   }
  // }
};


export const httpInterceptorFn: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {

  const service = {
    sessionService: inject(SessionService),
    router: inject(Router),
    commonFacade: inject(CommonFacade),
    toastController: inject(ToastController),
    api: request.url.split('/')[request.url.split('/').length - 1],
    cancel: inject(CancelApiService)
  };

  // for cypress e2e test, bypass all API request with dummy URL
  if (window.Cypress) {
    Object.assign(request, request.clone(
      {
        ...headers(service, request),
        url: request.url.replace(inject(BASE_URL), 'cypress/test')
      }
    ));
  }
  else {
    Object.assign(request, request.clone(
      headers(
        service,
        request,
        service.api === GUEST,
        service.api === 'GetState' && service.sessionService.getUserEmail() === GUEST
      )
    ));
  }

  // console.log('request:', request)

  const GUEST_USER_API_ACCESS_LIST = [
    'Login',
    'Signup',
    'RenewPasswordLink',
    'UpdatePassword',
    'VerifiyIdentity',
    'GetEventsDetails',
    'GetRace',
  ];

  // Check if Guest User can proceed on API call
  if (service.api === 'GetState' || service.api === GUEST) {
    null; // continue process
  }
  else if (service.sessionService.getUserEmail() === GUEST) {
    if (GUEST_USER_API_ACCESS_LIST.includes(service.api)) null; // has access
    else if ("GetPoolMatrixV2" === service.api) {
      // for Guest user, only fetch the WIN pool for the Odds

      const url = new URL(request.urlWithParams);
      const params = new URLSearchParams(url.search);

      let hasWIN = false;

      params.forEach((value, key) => {
        if (key === 'poolId' && value === 'WIN') hasWIN = true;
      });

      if (hasWIN) null;  // has access
      else return EMPTY; // stop Guest User unnecessary API call
    }
    else return EMPTY; //  stop Guest User unnecessary API call
  }
  // Remove to fix persisting loading UI from Login issue
  // else if (service.sessionService.getUserEmail() !== GUEST) {
  //   if (!service.sessionService.hasToken()) return EMPTY; //  stop request if no token
  // }

  return next(request)
    .pipe(
      // takeUntil(service.cancel.cancelObservable$), // Cancels the request when a signal is emitted
      // Error handling
      catchError((errorResp) => {

        // show Message if errorResp.error has no data
        if (!errorResp?.error) {
          errorResp = { ...errorResp, MessageCode: 'temp'};
        }

        // show Message if MessageCode not exists as property
        try {
          // eslint-disable-next-line no-unsafe-optional-chaining
          if (!("MessageCode" in errorResp?.error)) {
            errorResp = { ...errorResp, MessageCode: 'temp', try: true};
          }
        } catch (error) {
          console.error("no-unsafe-optional-chaining")
        }

        // show Message if MessageCode is falsy
        // skip if already added from try catch
        if (!errorResp?.error?.MessageCode && !errorResp?.try) {
          errorResp = {
            ...errorResp,
            error: {
              ...errorResp?.error,
              MessageCode: 'temp'
            },
            MessageCode: 'temp'
          };
        }


        // for QA purpose only, for bug tracking
        service.commonFacade.qaTesting$()
          .pipe(take(1))
          .subscribe((data: any) => {

            if (!data?.showStatusCodeInToastMessage) return;
            testQApresentToast(
              `
                [STATUS_CODE]=${errorResp?.status}
                [API]=${errorResp?.url.split('/api')[1]}
                [MESSAGE_CODE]=${errorResp?.error?.MessageCode}
                [MESSAGE]=${errorResp?.error?.Message}
                [OTHER_ERROR]=${errorResp?.error?.Error}
                [DETAILS]=${errorResp?.error?.Details}
              `,
              service.toastController
            );
          })

        // console.log('xxx:', errorResp);

        if (request.url.includes('GetState')) {
          // if API has 'GetState'
          null;
          // Note: ignore GetState handling if there's error.
          // Execute the handling under app.component.ts
        }
        // for reply and live video error message from API reponse.
        // to avoid refactor PM michael code.
        else if (
          errorResp?.url.includes('VideoStreaming') && statusCodeChecker(errorResp?.status) &&
          service.sessionService.getRoleId() !== 10
        ) {
          errorResp = { ...errorResp, from: false};
          service.commonFacade.setErrorAPI({
            from: true,
            statusCode: errorResp.status,
            message: errorResp?.error?.Message,
            hasError: true
          });
        }
        else if (
          errorResp.status === 404 && !errorResp.error?.MessageCode &&
          !errorResp?.url.includes('VideoStreaming')
        ) {
          // handle error 404 here if no MessageCode
          errorResp = { ...errorResp, MessageCode: SERVER_ERROR_MESSAGE};

          if (!apiWithNoFloatingMessageList.includes(service.api)) {
            service.commonFacade.setErrorAPI({
              statusCode: errorResp.status,
              message: errorResp?.MessageCode,
              hasError: true
            });
          }
        }
        else if (((service.api !== 'Login') && errorResp.status === 401 && service.sessionService.hasToken()) ||
          (!apiWithNoUserTokenNeededList.includes(service.api) && !service.sessionService.hasToken()))
        {
          if (PAGES_WITH_401_STATUS_CODE_BUT_SKIP_WAGER_REDIRECT.includes(service.router.url)) {
            // skip 401 status redirect
            null;
          }
          else {
            // Todo: separate this as standalone function, app.component is using
            // if Unauthorized API and has userToken or No userToken, then auto logout
            service.commonFacade.logError(errorResp, 'Router guard!');
            // service.sessionService.resetLogout();
            // service.commonFacade.setIsAuthenticated(false, "httpInterceptorFn");
            if (service.sessionService.getChangePasswordIsActive()) {
              service.sessionService.resetLogout();
              service.commonFacade.setIsAuthenticated(false, "httpInterceptorFn");
            }
            else {
              service.sessionService.resetLogout();
              service.commonFacade.setIsAuthenticated(false, "httpInterceptorFn");
              service.router.navigate(['/wager']);
            }
          }
        }
        else if (apiWithNoUserTokenNeededList.includes(service.api)) {
          null;
          // process error handling in the specific component
          // ex. login.component.ts
        }
        else if (errorResp.status === 429) {
          null;
          // ignore showing popup error
          // 429 Too Many Requests
        }
        else if (String(errorResp.status)[0] === '4' || String(errorResp.status)[0] === '5') {
          // TODO: only temporary
          let APIS = [
            'EprotectInformation',
            'GetTransactionFee',
            'CreditCardDeposit',
            'MoneyPakDeposit',
            'GetAccountReportEx',
            'CreditCardWithdraw',
            'GetTicketsSlim', // to not show general floating error message,
            'CancelWager',
            'ACHDeposit',
            'ACHWithdrawal',
            'GetPendingACH',
            'GetFundingLimits',
            'LossLimit'
          ];

          if (errorResp.status === 400) {
            if (service.api === 'IssueSingleTicket') {
              APIS = [
                ...APIS,
                'IssueSingleTicket' // to not show general floating error message,
              ];
            }
          }

          if (
            (!APIS.includes(service.api) &&
            !errorResp?.url.includes('VideoStreaming')) &&
            !errorResp?.url.includes('LossLimit')
          ) {
            // if Server Error or Client Error
            service.commonFacade.setErrorAPI({
              statusCode: errorResp.status,
              message: SERVER_ERROR_MESSAGE,
              hasError: true
            });
          }
        }

        return throwError(() => errorResp);
      })
    );
}

// for Live/Reply Video
// catch all possible status code
const statusCodeChecker = (statusCode: number): boolean => {
  let result = false;

  if (statusCode >= 300 && statusCode < 400) {
    result = true;
  } else if (statusCode >= 400 && statusCode < 500) {
    result = true;
  } else if (statusCode >= 500 && statusCode < 600) {
    result = true;
  } else {
    // encase
  }

  return result;
}

// for QA purpose only, for bug tracking
const testQApresentToast = async (message = '', toastController: ToastController, position: 'top' | 'middle' | 'bottom' = 'top') => {
  const toast = await toastController.create({
    message,
    position,
    buttons: [
      {
        text: 'Dismiss',
        role: 'cancel',
      },
    ]
  });

  await toast.present();
}
